import type { MermaidElementModel } from '@blocksuite/affine-model';

export type MermaidTextDeltaOperation = {
  delete?: number;
  insert?: unknown;
  retain?: number;
};

export type MermaidTextPatch = {
  deleteCount: number;
  index: number;
  insertText: string;
};

export function getMermaidTextPatch(
  previous: string,
  next: string
): MermaidTextPatch {
  let prefix = 0;
  const maxPrefix = Math.min(previous.length, next.length);
  while (prefix < maxPrefix && previous[prefix] === next[prefix]) {
    prefix++;
  }

  let suffix = 0;
  const maxSuffix = Math.min(previous.length - prefix, next.length - prefix);
  while (
    suffix < maxSuffix &&
    previous[previous.length - 1 - suffix] === next[next.length - 1 - suffix]
  ) {
    suffix++;
  }

  return {
    index: prefix,
    deleteCount: previous.length - prefix - suffix,
    insertText: next.slice(prefix, next.length - suffix),
  };
}

export function applyMermaidSourceInput(
  model: MermaidElementModel,
  nextSource: string
) {
  const source = model.source;
  const previous = source.toString();
  if (previous === nextSource) return;

  const patch = getMermaidTextPatch(previous, nextSource);
  model.surface.store.transact(() => {
    if (patch.deleteCount > 0) {
      source.delete(patch.index, patch.deleteCount);
    }
    if (patch.insertText) {
      source.insert(patch.index, patch.insertText);
    }
  });
}

export function transformMermaidTextIndex(
  index: number,
  delta: MermaidTextDeltaOperation[],
  association: -1 | 1 = 1
) {
  let oldPosition = 0;
  let shift = 0;

  for (const operation of delta) {
    if (typeof operation.retain === 'number') {
      oldPosition += operation.retain;
      continue;
    }

    if (operation.insert !== undefined) {
      const inserted =
        typeof operation.insert === 'string' ? operation.insert.length : 1;
      if (oldPosition < index || (oldPosition === index && association > 0)) {
        shift += inserted;
      }
      continue;
    }

    if (typeof operation.delete === 'number') {
      if (oldPosition < index) {
        shift -= Math.min(operation.delete, index - oldPosition);
      }
      oldPosition += operation.delete;
    }
  }

  return Math.max(0, index + shift);
}

export function getMermaidErrorLocation(message: string) {
  const lineAndColumn =
    /\bline\s+(\d+)(?:\s*[,;:]?\s*(?:column|col)\s+(\d+))?/i.exec(message);
  if (lineAndColumn) {
    return {
      line: Number(lineAndColumn[1]),
      column: lineAndColumn[2] ? Number(lineAndColumn[2]) : undefined,
    };
  }

  const colonLocation = /(?:^|\s)(\d+):(\d+)(?:\s|$)/.exec(message);
  if (colonLocation) {
    return {
      line: Number(colonLocation[1]),
      column: Number(colonLocation[2]),
    };
  }

  return null;
}
